import { api } from '../config/api';

export enum CanalNotification {
  SMS = 'sms',
  WHATSAPP = 'whatsapp',
  EMAIL = 'email',
}

export enum StatutNotification {
  ENVOYE = 'envoye',
  ECHOUE = 'echoue',
  EN_ATTENTE = 'en_attente',
}

export enum CanalTemplate {
  SMS = 'sms',
  WHATSAPP = 'whatsapp',
  TOUS = 'tous',
}

export interface Notification {
  id: string;
  canal: CanalNotification;
  destinataire_nom: string;
  destinataire_telephone: string;
  message: string;
  template_id?: string;
  colis_id?: string;
  statut: StatutNotification;
  provider_message_id?: string;
  erreur?: string;
  credits_utilises: number;
  created_at: string;
}

export type MetaTemplateStatus = 'non_soumis' | 'en_attente' | 'approuve' | 'rejete';
export type MetaTemplateCategory = 'AUTHENTICATION' | 'UTILITY' | 'MARKETING';

export type AutoEventSlot =
  | 'otp'
  | 'creation_destinataire'
  | 'creation_expediteur'
  | 'livraison_destinataire'
  | 'livraison_expediteur'
  | 'arrivee_destinataire'
  | 'arrivee_expediteur'
  | 'en_livraison_destinataire'
  | 'sur_place_destinataire'
  | 'incident_destinataire'
  | 'incident_expediteur'
  | 'relance_destinataire';

export interface NotificationTemplate {
  id: string;
  nom: string;
  canal: CanalTemplate;
  contenu: string;
  variables: string[];
  actif: boolean;
  created_at: string;
  updated_at: string;
  meta_category: MetaTemplateCategory;
  meta_language: string;
  meta_template_name: string | null;
  meta_template_id: string | null;
  meta_status: MetaTemplateStatus;
  meta_rejection_reason: string | null;
  auto_event: AutoEventSlot | null;
}

export interface NotifStats {
  total: number;
  envoyes: number;
  echecs: number;
  total_sms: number;
  total_whatsapp: number;
  credits_total_utilises: number;
  wallet: {
    sms_credits_restants: number;
    whatsapp_credits_restants: number;
  };
}

export interface SendNotificationDto {
  canal: CanalNotification;
  destinataire_nom: string;
  destinataire_telephone?: string;
  destinataire_email?: string;
  message?: string;
  template_id?: string;
  variables?: Record<string, string>;
  colis_id?: string;
  photo_url?: string; // WhatsApp: sends image + caption
}

export const notificationService = {
  // Statistiques
  getStats: async (): Promise<NotifStats> => {
    const res = await api.get('/notifications/stats');
    return res.data;
  },

  // Historique
  getAll: async (filters?: any): Promise<Notification[]> => {
    const res = await api.get('/notifications', { params: filters });
    return res.data;
  },

  // Envoi manuel
  send: async (dto: SendNotificationDto): Promise<Notification> => {
    const res = await api.post('/notifications/send', dto);
    return res.data;
  },

  // Assistant de configuration WhatsApp (6 étapes)
  getWhatsAppSetupStatus: async (): Promise<{
    mode: 'propre' | 'plateforme';
    test_ok_at: string | null;
    templates: { total: number; approuves: number; en_attente: number; rejetes: number };
    slots_manquants: string[];
    etapes: { key: string; label: string; hint: string; done: boolean }[];
    done: number;
    total: number;
  }> => {
    const res = await api.get('/notifications/whatsapp/status');
    return res.data;
  },

  // Santé des envois automatiques (bannière d'alerte)
  getHealth: async (): Promise<{
    auto_actif: boolean;
    canal: string;
    sms: { ok: boolean; reason: string | null };
    whatsapp: { ok: boolean; mode: 'propre' | 'plateforme'; reason: string | null };
    echecs_7j: number;
    problems: string[];
  }> => {
    const res = await api.get('/notifications/health');
    return res.data;
  },

  // Wallet
  getWallet: async () => {
    const res = await api.get('/notifications/wallet');
    return res.data;
  },

  // Variables disponibles
  getVariables: async () => {
    const res = await api.get('/notifications/variables');
    return res.data;
  },

  // Templates
  getTemplates: async (): Promise<NotificationTemplate[]> => {
    const res = await api.get('/notifications/templates');
    return res.data;
  },
  getTemplate: async (id: string): Promise<NotificationTemplate> => {
    const res = await api.get(`/notifications/templates/${id}`);
    return res.data;
  },
  createTemplate: async (data: Partial<NotificationTemplate>): Promise<NotificationTemplate> => {
    const res = await api.post('/notifications/templates', data);
    return res.data;
  },
  updateTemplate: async (id: string, data: Partial<NotificationTemplate>): Promise<NotificationTemplate> => {
    const res = await api.patch(`/notifications/templates/${id}`, data);
    return res.data;
  },
  deleteTemplate: async (id: string) => {
    const res = await api.delete(`/notifications/templates/${id}`);
    return res.data;
  },

  // Approbation Meta (WhatsApp Business message templates)
  submitMetaTemplate: async (id: string): Promise<NotificationTemplate> => {
    const res = await api.post(`/notifications/templates/${id}/meta/submit`);
    return res.data;
  },
  syncMetaTemplateStatus: async (id: string): Promise<NotificationTemplate> => {
    const res = await api.post(`/notifications/templates/${id}/meta/sync`);
    return res.data;
  },
  syncAllMetaTemplateStatuses: async (): Promise<NotificationTemplate[]> => {
    const res = await api.post('/notifications/templates/meta/sync-all');
    return res.data;
  },
  unsubmitMetaTemplate: async (id: string): Promise<NotificationTemplate> => {
    const res = await api.delete(`/notifications/templates/${id}/meta`);
    return res.data;
  },
  seedEcommerceTemplates: async (): Promise<{ created: number; existing: number }> => {
    const res = await api.post('/notifications/templates/seed-ecommerce');
    return res.data;
  },
  setAutoEventTemplate: async (id: string, event: AutoEventSlot | null): Promise<NotificationTemplate> => {
    const res = await api.patch(`/notifications/templates/${id}/auto-event`, { event });
    return res.data;
  },
};
